import { FC, useMemo, ReactElement, useCallback } from 'react'
import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { CryptoCurrencyId } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import { CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_ENABLE } from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { CryptoCurrencyLogo } from 'components/features/rewards/rewardsPane/containers/CryptoCurrencyLogo/CryptoCurrencyLogo'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import {
  getCryptoCurrenciesMetaDataWithMarketDataSelector,
  isCryptocurrenciesMetaListLoadingSelector,
} from 'components/features/rewards/rewardsPane/store/selectors/currencies'
import { useCryptoRewardsPaneModifyStateCallback } from 'components/features/rewards/rewardsPane/hooks/useCryptoRewardsPaneModifyFieldsStateCallback'
import { FLOATING_PANE_AUTO_INVESTMENTS_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import {
  CryptoRewardsCurrencyPickerWithMarketDetails,
} from 'components/features/rewards/rewardsPane/components/CryptoRewardsCurrencyPickerWithMarketDetails/CryptoRewardsCurrencyPickerWithMarketDetails'
import { getCryptoRewardsPaneProgressPercentsStateSelector } from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { cryptoMetaGuard } from 'components/features/rewards/rewardsPane/guards/cryptoMetaGuard'
import { cryptoMarketGuard } from 'components/features/rewards/rewardsPane/guards/cryptoCurrenciesMarketDataLoadedGuard'

type CryptoRewardsAutoInvestmentsCurrencyChoosingStepProps = {
  onClose: () => void
}

type CryptoRewardsAutoInvestmentsCurrencyChoosingStepReadyProps = CryptoRewardsAutoInvestmentsCurrencyChoosingStepProps & {
  onGoBack: () => void
}

const STEP_TITLE = 'Invest Your Rewards Into Crypto'

const STEP_SUBTITLE = 'Pick your cryptocurrency'

export const CryptoRewardsAutoInvestmentsCurrencyChoosingStepReady: FC<CryptoRewardsAutoInvestmentsCurrencyChoosingStepReadyProps> = ({
  onClose,
  onGoBack,
}) => {
  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)
  const currenciesMeta = useStoreSelector(getCryptoCurrenciesMetaDataWithMarketDataSelector)
  const isMarketDataLoaded = useSimpleGuard(cryptoMarketGuard, {}, 'CryptoRewardsBuyCurrencyListStepReady')
  const isCryptoMetaLoading = useStoreSelector(isCryptocurrenciesMetaListLoadingSelector)
  const isPaneDataLoading = isCryptoMetaLoading || !isMarketDataLoaded

  const getIconByCryptoId = useCallback((id: CryptoCurrencyId): ReactElement => <CryptoCurrencyLogo currencyId={ id } />, [])
  const modifyPaneState = useCryptoRewardsPaneModifyStateCallback(FLOATING_PANE_AUTO_INVESTMENTS_MODE)
  const handleCryptoCurrencyChosen = useCallback((id: CryptoCurrencyId) => {
    modifyPaneState({
      step: CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_ENABLE,
      fields: { cryptoCurrencyIDChosenForInvestment: id },
    })
  }, [modifyPaneState])
  const currenciesTable = useMemo<ReactElement>(() => (
    <CryptoRewardsCurrencyPickerWithMarketDetails
      isLoading={ isPaneDataLoading }
      getIconByCryptoId={ getIconByCryptoId }
      onChoose={ handleCryptoCurrencyChosen }
    >
      { currenciesMeta }
    </CryptoRewardsCurrencyPickerWithMarketDetails>
  ), [currenciesMeta, isPaneDataLoading, getIconByCryptoId, handleCryptoCurrencyChosen])

  return (
    <CryptoRewardsPanePreset
      title={ STEP_TITLE }
      subTitle={ STEP_SUBTITLE }
      progressBarPercent={ paneProgressPercent }
      onCancel={ onClose }
      onFooterCancel={ onGoBack }
    >
      { currenciesTable }
    </CryptoRewardsPanePreset>
  )
}
export const CryptoRewardsAutoInvestmentsCurrencyChoosingStep: FC<CryptoRewardsAutoInvestmentsCurrencyChoosingStepProps> = ({ onClose }) => {
  const isCryptoCurrenciesMetaLoaded = useSimpleGuard(
    cryptoMetaGuard,
    {},
    'CryptoRewardsAutoInvestmentsCurrencyChoosingStep'
  )
  const isCryptoCurrenciesMetaDataWithMarketDataExists = useSimpleGuard(
    cryptoMarketGuard,
    {},
    'CryptoRewardsAutoInvestmentsCurrencyChoosingStep'
  )
  const isStepRequiredDataLoading = !(isCryptoCurrenciesMetaLoaded && isCryptoCurrenciesMetaDataWithMarketDataExists)
  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)

  const modifyPaneState = useCryptoRewardsPaneModifyStateCallback(FLOATING_PANE_AUTO_INVESTMENTS_MODE)
  const handleCancelPressed = useCallback((): void => {
    modifyPaneState({
      step: CRYPTO_REWARDS_AUTO_INVESTMENTS_MODE_STEP_ENABLE,
    })
  }, [modifyPaneState])

  const stepNode = useMemo<ReactElement>(() => {
    if (isStepRequiredDataLoading) {
      return (
        <CryptoRewardsPanePreset
          isLoading
          title={ STEP_TITLE }
          subTitle={ STEP_SUBTITLE }
          progressBarPercent={ paneProgressPercent }
          cancelName='Back'
          onCancel={ onClose }
          onFooterCancel={ handleCancelPressed }
        />
      )
    }
    return <CryptoRewardsAutoInvestmentsCurrencyChoosingStepReady onClose={ onClose } onGoBack={ handleCancelPressed } />
  }, [isStepRequiredDataLoading, paneProgressPercent, onClose, handleCancelPressed])

  return stepNode
}
